import { APP_ROUTES } from "@/constants/app-routes";
import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetGoals } from "@/hooks/requests/goals/use-get-goals";
import { useUpdateGoals } from "@/hooks/requests/goals/use-update-goals";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useState } from "react";

const GOAL_OPTIONS = [
  {
    id: "weight-loss",
    label: "Lose weight",
    icon: "/assets/images/onboarding-v5/icons/goal-lose.svg",
  },
  {
    id: "weight-maintain",
    label: "Maintain weight",
    icon: "/assets/images/onboarding-v5/icons/goal-maintain.svg",
  },
  {
    id: "weight-gain",
    label: "Gain weight",
    icon: "/assets/images/onboarding-v5/icons/goal-gain.svg",
  },
];

const GoalPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { goals } = useGetGoals();
  const { updateGoalsAsync, isUpdating } = useUpdateGoals();
  const { showError } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = selectedId ?? goals?.specificWeightGoals ?? null;

  const handleContinue = async () => {
    if (!selected || isUpdating) return;
    triggerHaptic();
    try {
      await Promise.all([
        preloadNextRoute(),
        updateGoalsAsync({
          specificWeightGoals: selected,
          goalsWithNutriCoach: goals?.goalsWithNutriCoach ?? "",
        }),
      ]);
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What goal shall we work towards?"
      subtitle="Choose one for now. We can change it later."
      // The step router says the previous step is the name screen, but the medical
      // question sits between them.
      previousPath={APP_ROUTES.ONBOARDING_DIAGNOSTIC}
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={!selected || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {GOAL_OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.id}
            label={option.label}
            icon={option.icon}
            selected={selected === option.id}
            onClick={() => setSelectedId(option.id)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default GoalPage;
